import DateRangeCalendar from '@/components/customer/date-range-calendar';
import BottomSheet from '@/components/shared/bottom-sheet';
import { Button } from '@/components/ui/button';
import { buildChips, formatDate } from '@/features/search-bar/utils';
import Icon from '@/icons/icon';

interface Props {
    startDate: Date | null;
    endDate: Date | null;
    setDates: (start: Date | null, end: Date | null) => void;
    openCalendar: boolean;
    setOpenCalendar: (open: boolean) => void;
    showCheckoutError: boolean;
    setShowCheckoutError: (show: boolean) => void;
    calendarPos: { top: number; left: number };
    checkInRef: React.RefObject<HTMLDivElement | null>;
    calendarRef: React.RefObject<HTMLDivElement | null>;
    mobileCalendarRef: React.RefObject<HTMLDivElement | null>;
}

export function SearchDatePicker({
    startDate,
    endDate,
    setDates,
    openCalendar,
    setOpenCalendar,
    showCheckoutError,
    setShowCheckoutError,
    calendarPos,
    checkInRef,
    calendarRef,
    mobileCalendarRef,
}: Props) {
    return (
        <div
            ref={checkInRef}
            className="relative flex w-full shrink-0 cursor-pointer items-center gap-2 border-t px-4 py-4 sm:w-auto sm:border-t-0 sm:border-l"
            onClick={() => setOpenCalendar(true)}
        >
            <Icon iconName="Calendar" className="h-5 w-5 stroke-gray-500" />

            {!startDate && !endDate ? (
                <p className="text-sm font-semibold text-gray-700">
                    Check-in — check-out
                </p>
            ) : (
                <p className="font-medium text-black dark:text-foreground">
                    {startDate ? formatDate(startDate) : ''}
                    {endDate ? ' – ' + formatDate(endDate) : ''}
                </p>
            )}

            {showCheckoutError && (
                <div className="absolute bottom-[calc(100%+4px)] left-0 z-60 animate-in duration-200 fade-in slide-in-from-bottom-1">
                    <div className="relative rounded-md bg-red-600 px-4 py-2 text-sm font-medium whitespace-nowrap text-white shadow-lg">
                        <div className="absolute -bottom-1 left-6 h-2 w-2 rotate-45 bg-red-600" />
                        Select a check-out date.
                    </div>
                </div>
            )}

            {openCalendar && (
                <>
                    {/* Mobile: bottom sheet */}
                    <div className="sm:hidden">
                        <BottomSheet
                            title="Select dates"
                            titleClassName="text-lg"
                            onClose={() => {
                                setOpenCalendar(false);
                                setShowCheckoutError(false);
                            }}
                            sheetRef={mobileCalendarRef}
                            backdropClassName="z-9999"
                            className="z-10000 animate-in duration-300 slide-in-from-bottom"
                            style={{ height: '91dvh' }}
                        >
                            {/* Calendar scroll area — 13 months */}
                            <div className="w-full flex-1 overflow-y-auto">
                                <DateRangeCalendar
                                    scrollableYear
                                    hideQuickSelect
                                    startDate={startDate}
                                    endDate={endDate}
                                    initialScrollDate={startDate}
                                    onChange={(s, e) => {
                                        setDates(s, e);
                                        if (e) setShowCheckoutError(false);
                                        // Don't auto-close — user taps Done
                                    }}
                                />
                            </div>

                            {/* Bottom sticky bar */}
                            <div className="shrink-0 border-t border-gray-100 bg-card px-4 pt-3 pb-6">
                                {/* Quick-select chips */}
                                <div className="no-scrollbar mb-3 flex gap-2 overflow-x-auto pb-1">
                                    {buildChips().map(({ label, action }) => (
                                        <Button
                                            variant="bare"
                                            key={label}
                                            type="button"
                                            onPointerDown={(e) => {
                                                e.stopPropagation();
                                                action((s, e) => {
                                                    setDates(s, e);
                                                    setShowCheckoutError(false);
                                                });
                                            }}
                                            className="shrink-0 cursor-pointer rounded-full border border-gray-200 px-4 py-1.5 text-sm font-semibold whitespace-nowrap text-gray-900 hover:bg-blue-50 active:bg-blue-100 dark:active:bg-blue-900/40"
                                        >
                                            {label}
                                        </Button>
                                    ))}
                                </div>

                                {startDate && endDate ? (
                                    <p className="mb-3 text-center text-sm text-gray-600">
                                        {formatDate(startDate)}{' '}
                                        <span className="text-gray-400">–</span>{' '}
                                        {formatDate(endDate)}{' '}
                                        <span className="font-semibold text-gray-900">
                                            (
                                            {Math.round(
                                                (endDate.getTime() -
                                                    startDate.getTime()) /
                                                    86400000,
                                            )}
                                            -night stay)
                                        </span>
                                    </p>
                                ) : startDate ? (
                                    <p className="mb-3 text-center text-sm text-gray-500">
                                        Select a check-out date
                                    </p>
                                ) : (
                                    <p className="mb-3 text-center text-sm text-gray-500">
                                        Select check-in date
                                    </p>
                                )}
                                <Button
                                    variant="bare"
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (startDate && !endDate) {
                                            setShowCheckoutError(true);
                                            return;
                                        }
                                        setOpenCalendar(false);
                                        setShowCheckoutError(false);
                                    }}
                                    className="w-full cursor-pointer rounded-xl bg-blue-600 py-3 text-base font-semibold text-white transition hover:bg-blue-700 active:bg-blue-800"
                                >
                                    Done
                                </Button>
                            </div>
                        </BottomSheet>
                    </div>

                    {/* Desktop: dropdown */}
                    <div
                        ref={calendarRef}
                        className="fixed z-9999 hidden rounded-xl bg-card shadow-2xl sm:block"
                        style={{
                            top: calendarPos.top,
                            left: calendarPos.left,
                        }}
                    >
                        <DateRangeCalendar
                            startDate={startDate}
                            endDate={endDate}
                            onChange={(s, e) => {
                                setDates(s, e);
                                if (e) setShowCheckoutError(false);
                                if (s && e) {
                                    setOpenCalendar(false);
                                }
                            }}
                        />
                    </div>
                </>
            )}
        </div>
    );
}
